import { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  FiSearch,
  FiGlobe,
  FiZap,
  FiBell,
  FiUser,
  FiCheck,
  FiGift,
  FiLogOut,
  FiUsers,
  FiCopy,
  FiLink,
  FiShield,
  FiCreditCard,
  FiKey,
  FiSettings,
  FiPieChart,
} from 'react-icons/fi';
import { Basketball, Football, Baseball, Hockey, SoccerBall, BoxingGlove } from '@phosphor-icons/react';
import { useAuth } from '../../contexts/AuthContext';
import { formatLocalDate, parseDateInput } from '../../utils/formatters';
import { bonusPoolApi } from '../../services/bonusPoolApi';
import { api } from '../../services/api';
import { WalletModal } from '../wallet';
import { PortfolioModal } from '../trading';
import { PointsLedgerModal } from '../points/PointsLedgerModal';
import { SecurityCheckModal } from '../security/SecurityCheckModal';
import { AccountModal, ApiKeysModal } from '../account';
import { isChineseEnabled } from '../../i18n';

const ALL_LANGUAGES = [
  { code: 'zh', label: '中文', flag: '🇨🇳' },
  { code: 'ja', label: '日本語', flag: '🇯🇵' },
  { code: 'es', label: 'Español', flag: '🇪🇸' },
  { code: 'en', label: 'English', flag: '🇺🇸' },
];
const LANGUAGES = isChineseEnabled 
  ? ALL_LANGUAGES 
  : ALL_LANGUAGES.filter(lang => lang.code !== 'zh');

export function Header({
  searchQuery,
  onSearchChange,
  activeSport,
  onSportChange,
  activeFilter,
  onFilterChange,
  filterCounts
}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuth();
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const langMenuRef = useRef(null);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);
  const [avatarError, setAvatarError] = useState(false);
  const [myDrawsOpen, setMyDrawsOpen] = useState(false);
  const [myDrawsLoading, setMyDrawsLoading] = useState(false);
  const [myDrawsError, setMyDrawsError] = useState('');
  const [myDraws, setMyDraws] = useState([]);
  const [myInvitesOpen, setMyInvitesOpen] = useState(false);
  const [inviteSummary, setInviteSummary] = useState(null);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteError, setInviteError] = useState('');
  const [inviteCopied, setInviteCopied] = useState(false);
  const [walletModalOpen, setWalletModalOpen] = useState(false);
  const [portfolioModalOpen, setPortfolioModalOpen] = useState(false);
  const [autoExportAddress, setAutoExportAddress] = useState(null);
  const [toast, setToast] = useState(null);
  const [pointsBalance, setPointsBalance] = useState(null);
  const [pointsLoading, setPointsLoading] = useState(false);
  const [pointsLedgerOpen, setPointsLedgerOpen] = useState(false);
  const [pointsLedgerItems, setPointsLedgerItems] = useState([]);
  const [pointsLedgerLoading, setPointsLedgerLoading] = useState(false);
  const [pointsLedgerError, setPointsLedgerError] = useState('');
  const [pointsLedgerHasMore, setPointsLedgerHasMore] = useState(false);
  const [pointsLedgerLoadingMore, setPointsLedgerLoadingMore] = useState(false);
  const [pointsLedgerFilter, setPointsLedgerFilter] = useState('all');
  const [securityCheckOpen, setSecurityCheckOpen] = useState(false);
  const [accountModalOpen, setAccountModalOpen] = useState(false);
  const [apiKeysModalOpen, setApiKeysModalOpen] = useState(false);
  const pointsLedgerPageSize = 20;

  const pushToast = useCallback((message, type = 'info') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  const changeLanguage = (langCode) => {
    i18n.changeLanguage(langCode);
    setLangMenuOpen(false);
  };

  const avatarUrl = user?.profile?.avatar_url;
  const username = user?.profile?.username || '';
  const displayName = user?.profile?.display_name || '';
  const avatarLabel = username || displayName;
  const avatarLetter = avatarLabel.trim().slice(0, 1).toUpperCase();
  const showAvatarImage = avatarUrl && !avatarError;
  const telegramHandle = username ? `@${username}` : '';
  const telegramDisplayName = displayName || (telegramHandle ? telegramHandle : t('header.telegramHandleMissing'));
  const showTelegramHandle = Boolean(displayName && telegramHandle && displayName !== username);

  useEffect(() => {
    setAvatarError(false);
  }, [avatarUrl]);

  useEffect(() => {
    if (!isAuthenticated) return;
    const pendingRaw = localStorage.getItem('wallet_export_pending');
    if (!pendingRaw) return;
    let pendingAddress = null;
    let pendingUserId = null;
    try {
      const parsed = JSON.parse(pendingRaw);
      pendingAddress = parsed?.address || null;
      pendingUserId = parsed?.user_id || null;
    } catch {
      pendingAddress = pendingRaw;
    }
    if (!pendingAddress) return;
    if (pendingUserId && pendingUserId !== user?.user_id) {
      localStorage.removeItem('wallet_export_pending');
      return;
    }
    setAutoExportAddress(pendingAddress);
    setWalletModalOpen(true);
  }, [isAuthenticated, user?.user_id]);

  useEffect(() => {
    if (!isAuthenticated || !user?.user_id) {
      setSecurityCheckOpen(false);
      return;
    }
    const pendingUserId = sessionStorage.getItem('security_check_pending');
    if (!pendingUserId || pendingUserId !== user.user_id) {
      return;
    }
    sessionStorage.removeItem('security_check_pending');
    if (user?.profile?.email) {
      setSecurityCheckOpen(false);
      return;
    }
    setSecurityCheckOpen(true);
  }, [isAuthenticated, user?.user_id, user?.profile?.email]);

  const handleAutoExportComplete = useCallback(() => {
    localStorage.removeItem('wallet_export_pending');
    setAutoExportAddress(null);
  }, []);

  const bonusPoolUrl = import.meta.env.VITE_BONUS_POOL_URL || '/bonus-pool';
  const isExternalBonusPool =
    bonusPoolUrl.startsWith('http://') || bonusPoolUrl.startsWith('https://');
  const bonusPoolPath = bonusPoolUrl.startsWith('/') ? bonusPoolUrl : `/${bonusPoolUrl}`;
  const inviteLink = inviteSummary?.inviteCode
    ? `${window.location.origin}/bonus-pool/invite/${inviteSummary.inviteCode}`
    : '';

  const membershipLabelMap = {
    free: t('header.membershipFree'),
    lite: t('header.membershipLite'),
    pro: t('header.membershipPro'),
  };
  const membershipLabel =
    user?.is_expired
      ? t('membership.expired')
      : user?.tier_info?.display_name ||
        membershipLabelMap[user?.tier_name] ||
        user?.tier_name ||
        t('header.membershipFree');
  const qualifiedInvites = inviteSummary?.qualifiedInvites ?? 0;
  const inviteRequired = inviteSummary?.inviteRequired ?? 5;
  const eligibleTickets = inviteSummary?.eligibleTickets ?? 0;
  const inviteRemainder = inviteRequired > 0 ? qualifiedInvites % inviteRequired : 0;
  const inviteRemaining =
    inviteRequired > 0
      ? inviteRemainder === 0
        ? inviteRequired
        : inviteRequired - inviteRemainder
      : 0;
  const inviteList = inviteSummary?.invites || [];
  const membershipBadgeTone = (tier) => {
    if (tier === 'pro') return 'border-amber-300/40 bg-amber-300/10 text-amber-200';
    if (tier === 'lite') return 'border-accent/40 bg-accent/15 text-accent-soft';
    return 'border-slate-500/30 bg-slate-500/10 text-slate-300';
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (langMenuRef.current && !langMenuRef.current.contains(event.target)) {
        setLangMenuOpen(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = useCallback(() => {
    // 先关闭菜单
    setUserMenuOpen(false);
    // 执行登出（清除 tokens 和用户状态）
    logout();
    // 导航到登录页（因为首页现在也需要登录）
    navigate('/login');
  }, [logout, navigate]);

  const handleCopyInvite = useCallback(async () => {
    if (!inviteLink) {
      return;
    }
    try {
      await navigator.clipboard.writeText(inviteLink);
      setInviteCopied(true);
      setTimeout(() => setInviteCopied(false), 2000);
    } catch {
      setInviteCopied(false);
    }
  }, [inviteLink]);

  const getRoundStatusLabel = (status) => {
    if (status === 'ongoing') return t('bonusPool.statusOngoing');
    if (status === 'pending_draw') return t('bonusPool.statusPending');
    if (status === 'ended') return t('bonusPool.statusEnded');
    return status;
  };

  const getClaimStatusLabel = (roundStatus, prize) => {
    if (prize?.claimStatus === 'pending_invite') {
      return t('bonusPool.claimStatusPendingInvite');
    }
    if (prize?.claimStatus === 'pending_payout') {
      return t('bonusPool.claimStatusPendingPayout');
    }
    if (prize?.claimStatus === 'paid') {
      return t('bonusPool.claimStatusPaid');
    }
    return roundStatus === 'ended'
      ? t('bonusPool.claimStatusNoPrize')
      : t('bonusPool.claimStatusPending');
  };

  const formatShortDate = (value) => {
    if (!value) return '-';
    const date = parseDateInput(value);
    if (!date) return '-';
    return formatLocalDate(date, { year: 'numeric', month: 'numeric', day: 'numeric' });
  };

  const formatAmount = (value) => {
    if (typeof value !== 'number') {
      return '-';
    }
    const formatted = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(value);
    return `${formatted}U`;
  };

  const loadMyDraws = useCallback(async () => {
    if (!isAuthenticated) {
      setMyDraws([]);
      return;
    }
    setMyDrawsLoading(true);
    setMyDrawsError('');
    const roundsRes = await bonusPoolApi.getRounds();
    const responseCode = roundsRes.data?.code;
    const isSuccess =
      responseCode === 'SUCCESS' || responseCode === 0 || responseCode === '0';
    if (!roundsRes.ok || !isSuccess) {
      setMyDrawsError(roundsRes.data?.msg || t('common.error'));
      setMyDrawsLoading(false);
      return;
    }
    const rounds = roundsRes.data?.data?.rounds || [];
    const myRounds = rounds.filter((round) => (round.myTicketCount || 0) > 0);
    const detailResults = await Promise.all(
      myRounds.map(async (round) => {
        const detailRes = await bonusPoolApi.getRoundDetail(round.id);
        const detailCode = detailRes.data?.code;
        const detailOk =
          detailCode === 'SUCCESS' || detailCode === 0 || detailCode === '0';
        if (!detailRes.ok || !detailOk) {
          return { ...round, myPrize: null };
        }
        return { ...round, myPrize: detailRes.data?.data?.myPrize || null };
      })
    );
    setMyDraws(detailResults);
    setMyDrawsLoading(false);
  }, [isAuthenticated, t]);

  useEffect(() => {
    if (!isAuthenticated || !userMenuOpen) {
      return;
    }
    let active = true;
    setPointsLoading(true);
    api.getPointsBalance()
      .then((data) => {
        if (!active) return;
        setPointsBalance(data?.data?.balance ?? 0);
      })
      .catch(() => {
        if (!active) return;
        setPointsBalance(null);
      })
      .finally(() => {
        if (!active) return;
        setPointsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [isAuthenticated, userMenuOpen]);

  const loadPointsLedger = useCallback(async ({ append = false } = {}) => {
    if (!isAuthenticated) {
      setPointsLedgerItems([]);
      return;
    }
    if (append) {
      setPointsLedgerLoadingMore(true);
    } else {
      setPointsLedgerLoading(true);
      setPointsLedgerError('');
    }
    try {
      const skip = append ? pointsLedgerItems.length : 0;
      const direction = pointsLedgerFilter === 'all' ? null : pointsLedgerFilter;
      const res = await api.getPointsLedger(pointsLedgerPageSize, skip, direction);
      if (res?.code === 'SUCCESS') {
        const newItems = res?.data?.items || [];
        setPointsLedgerItems((prev) => (append ? [...prev, ...newItems] : newItems));
        setPointsLedgerHasMore(newItems.length === pointsLedgerPageSize);
      } else {
        setPointsLedgerError(res?.msg || t('points.ledgerFailed', '加载失败'));
      }
    } catch (err) {
      setPointsLedgerError(err?.message || t('points.ledgerFailed', '加载失败'));
    } finally {
      setPointsLedgerLoading(false);
      setPointsLedgerLoadingMore(false);
    }
  }, [isAuthenticated, pointsLedgerFilter, pointsLedgerItems.length, t]);

  useEffect(() => {
    if (pointsLedgerOpen) {
      loadPointsLedger();
    } else {
      setPointsLedgerItems([]);
      setPointsLedgerHasMore(false);
    }
  }, [pointsLedgerOpen, loadPointsLedger]);

  useEffect(() => {
    if (pointsLedgerOpen) {
      loadPointsLedger();
    }
  }, [pointsLedgerFilter, pointsLedgerOpen, loadPointsLedger]);

  const loadInviteSummary = useCallback(async () => {
    if (!isAuthenticated) {
      setInviteSummary(null);
      return;
    }
    setInviteLoading(true);
    setInviteError('');
    const summaryRes = await bonusPoolApi.getInviteSummary();
    const responseCode = summaryRes.data?.code;
    const isSuccess =
      responseCode === 'SUCCESS' || responseCode === 0 || responseCode === '0';
    if (!summaryRes.ok || !isSuccess) {
      setInviteError(summaryRes.data?.msg || t('common.error'));
      setInviteLoading(false);
      return;
    }
    setInviteSummary(summaryRes.data?.data || null);
    setInviteLoading(false);
  }, [isAuthenticated, t]);

  useEffect(() => {
    if (!myDrawsOpen) {
      return;
    }
    loadMyDraws();
  }, [myDrawsOpen, loadMyDraws]);

  useEffect(() => {
    if (!myInvitesOpen) {
      return;
    }
    setInviteCopied(false);
    loadInviteSummary();
  }, [myInvitesOpen, loadInviteSummary]);

  const handleLogoClick = () => {
    navigate('/');
  };

  const sports = [
    { id: 'nba', label: 'NBA', enabled: true, live: true },
    { id: 'nfl', label: 'NFL', enabled: false },
    { id: 'mlb', label: 'MLB', enabled: false },
    { id: 'nhl', label: 'NHL', enabled: false },
    { id: 'soccer', label: t('sports.soccer'), enabled: false },
    { id: 'mma', label: 'MMA', enabled: false },
  ];

  const filters = [
    { id: 'all', label: t('common.all'), count: filterCounts?.all },
    { id: 'live', label: t('common.live'), count: filterCounts?.live, dot: true },
    { id: 'upcoming', label: t('common.upcoming'), count: filterCounts?.upcoming },
    { id: 'ended', label: t('common.ended'), count: filterCounts?.ended },
  ];

  return (
    <header className="sticky top-0 z-50 transition-all duration-300">
      <div className="absolute inset-0 bg-surface/70 backdrop-blur-glass border-b border-glass-border"></div>

      {/* Top row: Logo, Search, Quick Links, Icons */}
      <div className="relative h-16 flex items-center justify-between px-6 max-w-[2000px] mx-auto">
        <div className="flex items-center gap-8">
          <div
            onClick={handleLogoClick}
            className="flex items-center gap-2 group cursor-pointer hover:opacity-80 transition-opacity"
          >
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-accent-strong to-accent-soft flex items-center justify-center text-white text-lg shadow-[0_6px_16px_rgba(8,12,18,0.35)]">
              <Basketball size={20} weight="duotone" />
            </div>
            <h1 className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-white to-gray-400 hidden lg:block">
              PolySports
            </h1>
          </div>
        </div>

        {/* Center: Search & Quick Links */}
        <div className="flex items-center gap-4 flex-1 justify-center max-w-2xl mx-auto px-4">
          <div className="relative group hidden md:block w-full max-w-xs transition-all duration-300 focus-within:max-w-md">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-accent-soft transition-colors" />
            <input
              type="text"
              placeholder={t('common.search')}
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              className="bg-surface-highlight/50 border border-glass-border rounded-xl pl-10 pr-4 py-2 text-sm w-full transition-all duration-300 focus:outline-none focus:border-accent-strong/50 focus:bg-surface-highlight focus:shadow-[0_0_14px_-5px_rgba(79,110,143,0.35)]"
            />
          </div>

          
        </div>

        <div className="flex items-center gap-4">
          {/* Action Icons */}
          <div className="flex items-center gap-2 pl-4">
            <button
              className="hidden md:inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-accent-warm/30 text-accent-warm/90 hover:text-white hover:bg-accent-warm/10 transition-colors"
              onClick={() => {
                if (isExternalBonusPool) {
                  window.open(bonusPoolUrl, '_blank', 'noopener');
                  return;
                }
                navigate(bonusPoolPath);
              }}
            >
              <FiGift className="w-4 h-4" />
              <span className="text-xs font-semibold">{t('common.bonusPool')}</span>
            </button>
            <button
              className="hidden md:inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-accent-strong/30 text-accent-soft hover:text-white hover:bg-accent-strong/10 transition-colors"
              onClick={() => {
                if (!isAuthenticated) {
                  navigate('/login');
                  return;
                }
                setMyInvitesOpen(true);
              }}
            >
              <FiUsers className="w-4 h-4 text-accent-soft" />
              <span className="text-xs font-semibold">{t('header.myInvites')}</span>
            </button>
            <button className="p-2.5 rounded-xl hover:bg-glass-hover text-neutral hover:text-accent-warm transition-colors relative group">
              <FiZap className="w-5 h-5" />
              <div className="absolute inset-0 bg-accent-warm/20 blur opacity-0 group-hover:opacity-100 transition-opacity rounded-xl" />
            </button>
            <button className="p-2.5 rounded-xl hover:bg-glass-hover text-gray-400 hover:text-white transition-colors relative">
              <div className="absolute top-2.5 right-2.5 w-2 h-2 bg-negative rounded-full border-2 border-surface" />
              <FiBell className="w-5 h-5" />
            </button>

            {/* Language Dropdown */}
            <div 
              ref={langMenuRef}
              className="relative z-[100]"
              onMouseEnter={() => setLangMenuOpen(true)}
              onMouseLeave={() => setLangMenuOpen(false)}
            >
              <button
                className="flex items-center gap-2 px-3 py-2 rounded-xl hover:bg-glass-hover text-gray-300 hover:text-white transition-colors border border-transparent hover:border-glass-border"
              >
                <FiGlobe className="w-4 h-4" />
                <span className="text-xs font-medium uppercase tracking-wider">{i18n.language}</span>
              </button>
              
              {/* Dropdown Menu - pt-1 creates invisible hover bridge */}
              {langMenuOpen && (
                <div className="absolute right-0 top-full pt-1 z-[100]">
                  <div className="py-1 min-w-[140px] bg-surface border border-glass-border rounded-xl shadow-lg backdrop-blur-glass overflow-hidden">
                    {LANGUAGES.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => changeLanguage(lang.code)}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
                        i18n.language === lang.code
                          ? 'bg-accent-strong/20 text-accent'
                          : 'text-gray-300 hover:bg-glass-hover hover:text-white'
                      }`}
                    >
                      <span className="text-base">{lang.flag}</span>
                      <span className="flex-1 text-left">{lang.label}</span>
                      {i18n.language === lang.code && (
                        <FiCheck className="w-4 h-4 text-accent" />
                      )}
                    </button>
                  ))}
                  </div>
                </div>
              )}
            </div>

            {/* User Avatar */}
            <div
              ref={userMenuRef}
              className="relative ml-2 z-[120]"
              onMouseEnter={() => setUserMenuOpen(true)}
              onMouseLeave={() => setUserMenuOpen(false)}
            >
              <button
                className="relative w-9 h-9 rounded-xl bg-gradient-to-br from-accent-strong to-accent flex items-center justify-center shadow-[0_8px_18px_rgba(8,12,18,0.35)] hover:scale-105 transition-transform border border-white/10 overflow-hidden"
                onClick={() => setUserMenuOpen((prev) => !prev)}
              >
                {showAvatarImage ? (
                  <img
                    src={avatarUrl}
                    alt={avatarLabel || 'avatar'}
                    className="h-full w-full object-cover"
                    onError={() => setAvatarError(true)}
                  />
                ) : avatarLetter ? (
                  <span className="text-sm font-semibold text-white">
                    {avatarLetter}
                  </span>
                ) : (
                  <FiUser className="w-4 h-4 text-white" />
                )}
              </button>
              {/* 会员等级标识 */}
              {user?.tier_name && user.tier_name !== 'free' && (
                <div className={`absolute -top-1 -right-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide shadow-lg ${
                  user.tier_name === 'pro' 
                    ? 'bg-gradient-to-r from-amber-400 to-amber-500 text-slate-900' 
                    : 'bg-gradient-to-r from-accent-strong to-accent text-white'
                }`}>
                  {user.tier_name}
                </div>
              )}
              {userMenuOpen && (
                <div className="absolute right-0 top-full pt-2 z-[120]">
                  <div className="w-48 rounded-xl border border-glass-border bg-surface/95 p-2 shadow-lg backdrop-blur-glass">
                    <div className="px-3 py-2 rounded-lg border border-glass-border/60 bg-surface-highlight/40 mb-2">
                      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
                        {t('header.telegramAccount')}
                      </div>
                      <div className="mt-1 text-sm font-semibold text-white truncate">
                        {telegramDisplayName}
                      </div>
                      {showTelegramHandle && (
                        <div className="text-xs text-slate-400 truncate">{telegramHandle}</div>
                      )}
                    </div>
                    <button
                      className="w-full flex items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-slate-200 bg-glass-hover/40 hover:bg-glass-hover"
                      onClick={() => {
                        if (!isAuthenticated) {
                          navigate('/login');
                          return;
                        }
                        setPointsLedgerOpen(true);
                        setUserMenuOpen(false);
                      }}
                      type="button"
                    >
                      <FiZap className="h-4 w-4 text-amber-200" />
                      <span className="flex-1">{t('points.balance', '点数余额')}</span>
                      <span className="font-semibold text-white">
                        {pointsLoading ? '...' : pointsBalance ?? '-'}
                      </span>
                    </button>
                    <button
                      className="w-full flex items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-slate-200 hover:bg-glass-hover"
                      onClick={() => {
                        if (!isAuthenticated) {
                          navigate('/login');
                          return;
                        }
                        setMyDrawsOpen(true);
                        setUserMenuOpen(false);
                      }}
                    >
                      <FiGift className="h-4 w-4 text-accent-warm" />
                      {t('header.myDraws')}
                    </button>
                    <button
                      className="w-full flex items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-slate-200 hover:bg-glass-hover"
                      onClick={() => {
                        if (!isAuthenticated) {
                          navigate('/login');
                          return;
                        }
                        setWalletModalOpen(true);
                        setUserMenuOpen(false);
                      }}
                    >
                      <FiCreditCard className="h-4 w-4 text-accent" />
                      {t('header.myWallet')}
                    </button>
                    <button
                      className="w-full flex items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-slate-200 hover:bg-glass-hover"
                      onClick={() => {
                        if (!isAuthenticated) {
                          navigate('/login');
                          return;
                        }
                        setPortfolioModalOpen(true);
                        setUserMenuOpen(false);
                      }}
                    >
                      <FiPieChart className="h-4 w-4 text-positive" />
                      {t('portfolio.title')}
                    </button>
                    <button
                      className="w-full flex items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-slate-200 hover:bg-glass-hover"
                      onClick={() => {
                        if (!isAuthenticated) {
                          navigate('/login');
                          return;
                        }
                        setApiKeysModalOpen(true);
                        setUserMenuOpen(false);
                      }}
                    >
                      <FiKey className="h-4 w-4 text-violet-300" />
                      {t('account.apiKeys.title')}
                    </button>
                    <button
                      className="w-full flex items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-slate-200 hover:bg-glass-hover"
                      onClick={() => {
                        if (!isAuthenticated) {
                          navigate('/login');
                          return;
                        }
                        setAccountModalOpen(true);
                        setUserMenuOpen(false);
                      }}
                    >
                      <FiSettings className="h-4 w-4 text-slate-400" />
                      {t('account.title')}
                    </button>
                    <button
                      className="w-full flex items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-slate-200 hover:bg-glass-hover"
                      onClick={handleLogout}
                    >
                      <FiLogOut className="h-4 w-4 text-negative" />
                      {t('header.logout')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {myDrawsOpen && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/60 px-4">
          <div className="w-full max-w-2xl rounded-3xl border border-glass-border bg-surface/95 p-6 text-slate-100 shadow-[0_30px_80px_rgba(0,0,0,0.45)]">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-slate-500">
                  {t('header.myDrawsTitle')}
                </div>
                <div className="mt-2 text-2xl font-semibold text-white">
                  {t('header.myDraws')}
                </div>
                <div className="mt-1 text-sm text-slate-300">
                  {t('header.myDrawsSubtitle')}
                </div>
              </div>
              <button
                className="rounded-full border border-glass-border p-2 text-slate-300 hover:text-white"
                onClick={() => setMyDrawsOpen(false)}
              >
                ✕
              </button>
            </div>

            <div className="mt-6 space-y-3">
              {myDrawsLoading && (
                <div className="text-sm text-slate-300">{t('header.myDrawsLoading')}</div>
              )}
              {!myDrawsLoading && myDrawsError && (
                <div className="text-sm text-red-200">{myDrawsError}</div>
              )}
              {!myDrawsLoading && !myDrawsError && myDraws.length === 0 && (
                <div className="rounded-2xl border border-glass-border bg-surface/70 p-4 text-sm text-slate-300">
                  {t('header.myDrawsEmpty')}
                </div>
              )}
              {!myDrawsLoading &&
                !myDrawsError &&
                myDraws.map((round) => (
                  <div
                    key={round.id}
                    className="rounded-2xl border border-glass-border bg-surface/70 p-4"
                  >
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <div className="text-xs uppercase tracking-[0.2em] text-slate-500">
                          {round.id}
                        </div>
                        <div className="mt-1 text-sm text-slate-300">
                          {getRoundStatusLabel(round.status)}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-slate-500">
                          {t('header.myDrawsTickets')}
                        </div>
                        <div className="text-lg font-semibold text-white">
                          {round.myTicketCount || 0}
                        </div>
                      </div>
                    </div>

                    <div className="mt-3 flex flex-wrap items-center justify-between gap-3 text-sm text-slate-300">
                      <div>
                        <span className="text-slate-500">{t('header.myDrawsStatus')}: </span>
                        <span className="text-white">
                          {getClaimStatusLabel(round.status, round.myPrize)}
                        </span>
                      </div>
                      {round.myPrize?.amount != null && (
                        <div className="text-right">
                          <div className="text-xs text-slate-500">
                            {t('header.myDrawsPrize')}
                          </div>
                          <div className="text-sm font-semibold text-white">
                            {formatAmount(round.myPrize.amount)}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
            </div>

            <div className="mt-6 flex flex-wrap justify-end gap-2">
              <button
                className="rounded-xl border border-glass-border px-4 py-2 text-sm text-slate-200 hover:bg-glass-hover"
                onClick={() => setMyDrawsOpen(false)}
              >
                {t('bonusPool.claimClose')}
              </button>
              <button
                className="rounded-xl bg-gradient-to-r from-accent-strong to-accent px-4 py-2 text-sm font-semibold text-white"
                onClick={() => {
                  setMyDrawsOpen(false);
                  navigate('/bonus-pool/rounds');
                }}
              >
                {t('header.myDrawsGo')}
              </button>
            </div>
          </div>
        </div>
      )}

      {myInvitesOpen && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/60 px-4">
          <div className="w-full max-w-2xl rounded-3xl border border-glass-border bg-surface/95 p-6 text-slate-100 shadow-[0_30px_80px_rgba(0,0,0,0.45)]">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-slate-500">
                  {t('header.myInvitesTitle')}
                </div>
                <div className="mt-2 text-2xl font-semibold text-white">
                  {t('header.myInvites')}
                </div>
                <div className="mt-1 text-sm text-slate-300">
                  {t('header.myInvitesSubtitle')}
                </div>
              </div>
              <button
                className="rounded-full border border-glass-border p-2 text-slate-300 hover:text-white"
                onClick={() => setMyInvitesOpen(false)}
              >
                ✕
              </button>
            </div>

            <div className="mt-6 space-y-4">
              {inviteLoading && (
                <div className="text-sm text-slate-300">{t('header.myInvitesLoading')}</div>
              )}
              {!inviteLoading && inviteError && (
                <div className="text-sm text-red-200">{inviteError}</div>
              )}
              {!inviteLoading && !inviteError && !inviteSummary && (
                <div className="rounded-2xl border border-glass-border bg-surface/70 p-4 text-sm text-slate-300">
                  {t('header.myInvitesEmpty')}
                </div>
              )}
              {!inviteLoading && !inviteError && inviteSummary && (
                <div className="space-y-4">
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="rounded-2xl border border-glass-border bg-surface/70 p-4">
                      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-slate-500">
                        <FiLink className="h-3.5 w-3.5 text-accent-soft" />
                        {t('header.inviteCodeLabel')}
                      </div>
                      <div className="mt-2 text-lg font-semibold text-white">
                        {inviteSummary.inviteCode || t('header.inviteCodeMissing')}
                      </div>
                      <div className="mt-3 flex items-center gap-2">
                        <div className="flex-1 rounded-lg border border-white/10 bg-surface/80 px-3 py-2 text-xs text-slate-200">
                          {inviteLink || t('header.inviteCodeMissing')}
                        </div>
                        <button
                          onClick={handleCopyInvite}
                          className="inline-flex items-center gap-2 rounded-lg border border-glass-border px-3 py-2 text-xs text-slate-200 hover:bg-glass-hover"
                          disabled={!inviteLink}
                        >
                          <FiCopy className="h-3.5 w-3.5 text-accent-soft" />
                          {inviteCopied ? t('header.inviteCopied') : t('header.inviteCopy')}
                        </button>
                      </div>
                    </div>

                    <div className="rounded-2xl border border-glass-border bg-surface/70 p-4">
                      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-slate-500">
                        <FiShield className="h-3.5 w-3.5 text-accent-warm" />
                        {t('header.membershipStatus')}
                      </div>
                      <div className={`mt-2 text-lg font-semibold ${user?.is_expired ? 'text-rose-200' : 'text-white'}`}>
                        {membershipLabel}
                      </div>
                      {user?.expires_at && (
                        <div className={`mt-1 text-xs ${user?.is_expired ? 'text-rose-300' : 'text-slate-400'}`}>
                          {t(user?.is_expired ? 'membership.expiredAt' : 'header.membershipExpires', {
                            date: formatShortDate(user.expires_at),
                          })}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="grid gap-3 md:grid-cols-2">
                    <div className="rounded-xl border border-glass-border bg-surface/70 p-4">
                      <div className="text-xs text-slate-500">{t('header.inviteStatsTotal')}</div>
                      <div className="mt-2 text-2xl font-semibold text-white">
                        {inviteSummary.totalInvites ?? 0}
                      </div>
                    </div>
                    <div className="rounded-xl border border-glass-border bg-surface/70 p-4">
                      <div className="text-xs text-slate-500">{t('header.inviteProgressTitle')}</div>
                      <div className="mt-2 text-2xl font-semibold text-white">
                        {qualifiedInvites}/{inviteRequired}
                      </div>
                      <div className="mt-1 text-xs text-slate-500">
                        {t('header.inviteProgressHint', { count: inviteRemaining })}
                      </div>
                      {eligibleTickets > 0 && (
                        <div className="mt-1 text-xs text-slate-400">
                          {t('header.inviteProgressEarned', { count: eligibleTickets })}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="rounded-2xl border border-glass-border bg-surface/70 p-4">
                    <div className="text-xs uppercase tracking-[0.2em] text-slate-500">
                      {t('header.inviteListTitle')}
                    </div>
                    {inviteList.length === 0 ? (
                      <div className="mt-3 text-sm text-slate-300">
                        {t('header.inviteListEmpty')}
                      </div>
                    ) : (
                      <div className="mt-3 max-h-40 space-y-2 overflow-y-auto pr-1">
                        {inviteList.map((invite) => (
                          <div
                            key={`${invite.inviteeId}-${invite.createdAt}`}
                            className="flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-surface/80 px-3 py-2 text-sm text-slate-200"
                          >
                            <span className="font-semibold text-white">{invite.displayName}</span>
                            <div className="flex items-center gap-2 text-xs text-slate-400">
                              <span
                                className={`rounded-full border px-2 py-0.5 ${membershipBadgeTone(
                                  invite.membershipTier
                                )}`}
                              >
                                {membershipLabelMap[invite.membershipTier] ||
                                  invite.membershipTier ||
                                  t('header.membershipFree')}
                              </span>
                              <span>{formatShortDate(invite.createdAt)}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="mt-6 flex justify-end">
              <button
                className="rounded-xl border border-glass-border px-4 py-2 text-sm text-slate-200 hover:bg-glass-hover"
                onClick={() => setMyInvitesOpen(false)}
              >
                {t('bonusPool.claimClose')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Second row: Sports Tabs + Status Filters */}
      <div className="relative h-12 flex items-center justify-between px-6 border-b border-glass-border bg-surface/30 backdrop-blur-md max-w-[2000px] mx-auto">
        {/* Sports Tabs */}
        <div className="flex items-center gap-2 overflow-x-auto scrollbar-none flex-1 mr-4">
          {sports.map((sport) => (
            <button
              key={sport.id}
              onClick={() => sport.enabled && onSportChange(sport.id)}
              disabled={!sport.enabled}
              className={`group relative px-4 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-2 whitespace-nowrap ${activeSport === sport.id
                ? 'text-white'
                : sport.enabled
                  ? 'text-gray-400 hover:text-white'
                  : 'text-gray-600 cursor-not-allowed'
                }`}
            >
              {activeSport === sport.id && (
                <div className="absolute inset-0 bg-white/5 rounded-lg border border-white/5 shadow-sm" />
              )}

              <span className="relative z-10 flex items-center gap-2">
                <span className={`text-lg transition-transform duration-300 ${activeSport === sport.id ? 'scale-110' : 'group-hover:scale-110'}`}>
                  {sport.id === 'nba' && <Basketball size={20} weight="duotone" className="text-orange-500" />}
                  {sport.id === 'nfl' && <Football size={20} weight="duotone" className="text-amber-700" />}
                  {sport.id === 'mlb' && <Baseball size={20} weight="duotone" className="text-red-400" />}
                  {sport.id === 'nhl' && <Hockey size={20} weight="duotone" className="text-sky-400" />}
                  {sport.id === 'soccer' && <SoccerBall size={20} weight="duotone" className="text-green-400" />}
                  {sport.id === 'mma' && <BoxingGlove size={20} weight="duotone" className="text-red-500" />}
                </span>
                {sport.label}
              </span>

              {sport.live && (
                <span className="relative z-10 text-[10px] font-bold bg-negative/20 text-negative px-1.5 py-0.5 rounded border border-negative/20 animate-pulse">
                  LIVE
                </span>
              )}
              {!sport.enabled && (
                <span className="relative z-10 text-[10px] text-gray-600 border border-gray-700/50 px-1.5 py-0.5 rounded">
                  Soon
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Status Filters - Moved to second row right side */}
        <div className="hidden lg:flex items-center p-0.5 bg-surface-highlight/30 rounded-lg border border-white/5 shrink-0">
          {filters.map((filter) => (
            <button
              key={filter.id}
              onClick={() => onFilterChange(filter.id)}
              className={`px-3 py-1 rounded-md text-xs font-medium transition-all duration-300 flex items-center gap-2 ${activeFilter === filter.id
                ? 'bg-accent-strong/80 shadow-sm text-white'
                : 'text-gray-400 hover:text-white hover:bg-white/5'
                }`}
            >
              {filter.dot && activeFilter !== filter.id && (
                <span className="w-1.5 h-1.5 rounded-full bg-negative animate-pulse" />
              )}
              {filter.label}
              {filter.count !== undefined && (
                <span className={`text-[10px] px-1.5 py-0 rounded ${activeFilter === filter.id
                  ? 'bg-white/20 text-white'
                  : 'bg-black/20 text-gray-400'
                  }`}>
                  {filter.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div
          className={`fixed bottom-24 left-1/2 -translate-x-1/2 z-[200] px-4 py-3 rounded-xl shadow-lg ${
            toast.type === 'success'
              ? 'bg-emerald-500/90 text-white'
              : toast.type === 'error'
              ? 'bg-rose-500/90 text-white'
              : 'bg-sky-500/90 text-white'
          }`}
        >
          {toast.message}
        </div>
      )}

      <SecurityCheckModal
        open={securityCheckOpen}
        emailBound={Boolean(user?.profile?.email)}
        onBindEmail={() => {
          setSecurityCheckOpen(false);
          setAccountModalOpen(true);
        }}
        onClose={() => setSecurityCheckOpen(false)}
      />

      <AccountModal
        open={accountModalOpen}
        onClose={() => setAccountModalOpen(false)}
      />

      <ApiKeysModal
        open={apiKeysModalOpen}
        onClose={() => setApiKeysModalOpen(false)}
      />

      {/* Wallet Modal */}
      <WalletModal
        open={walletModalOpen}
        onClose={() => setWalletModalOpen(false)}
        pushToast={pushToast}
        autoExportAddress={autoExportAddress}
        onAutoExportComplete={handleAutoExportComplete}
      />

      {/* Portfolio Modal */}
      <PortfolioModal
        open={portfolioModalOpen}
        onClose={() => setPortfolioModalOpen(false)}
        pushToast={pushToast}
      />

      <PointsLedgerModal
        open={pointsLedgerOpen}
        onClose={() => setPointsLedgerOpen(false)}
        items={pointsLedgerItems}
        loading={pointsLedgerLoading}
        error={pointsLedgerError}
        filter={pointsLedgerFilter}
        onFilterChange={(value) => {
          setPointsLedgerFilter(value);
          setPointsLedgerHasMore(false);
        }}
        onLoadMore={() => loadPointsLedger({ append: true })}
        hasMore={pointsLedgerHasMore}
        loadingMore={pointsLedgerLoadingMore}
      />
    </header>
  );
}
